import {
    AfterViewInit,
    Component,
    EventEmitter,
    Inject,
    Input,
    OnInit,
    Output,
    ViewChild
} from "@angular/core";
import { NgForm, NgModel } from "@angular/forms";
import { CardNumberHostComponent } from "@app/components/card/card-number-host.component";
import { Def } from "@card/common/definitions";
import { AppDataService } from "@app/services/appData.service";
import { WindowToken } from "@app/util/window.token";
import { MatDialog } from "@angular/material/dialog";
import { debounce } from "@app/support/util/debounce.function";
import { MonthYearData, MonthYearSelectsComponent } from "../util/month-year-selects.component";
import { CardCcvHelperDialog } from "@app/pages/guestpayment/guestpaymentcard/dialogs/card-ccv-helper.dialog";
import { CardUpdate } from "@app/services/stores/GuestPaymentStore";

export type CardDetailEditProps = {

    /** this will be disabled if there is no existing card */
    disableClearCard?: boolean

    /** disable changing the expiration date */
    disableExpirationDate?: boolean
    /** disable changing the name on the card */
    disableNameOnCard?: boolean
    /** mark as default payment method */
    makeDefault: boolean,

    existingCard: CardUpdate
    addressDisabled: boolean,
    cardDisabled: boolean,
    nickName: string,
}

export type CardDetailEditData = {
    makeDefault: boolean;
    nameOnCard: string;
    expMonth: number;
    expYear: number;
    secCode: string;
    nickName?: string;
}

@Component({
    selector: 'app-card-detail-edit',
    template: require('./card-detail-edit.component.html')
})
export class CardDetailEditComponent implements OnInit, AfterViewInit {

    @Input() props: CardDetailEditProps;
    @Input() addressDisabled: boolean;
    @Input() cardDisabled: boolean;
    @Input() showNickName: boolean;
    @Input() showDefault = true;
    @Input() isGuest = false;

    @Output() onClearCard = new EventEmitter<void>();
    @Output() onFormComplete = new EventEmitter<boolean>();

    @ViewChild('cardDetailEditForm', { static: true }) cardDetailEditForm: NgForm;
    @ViewChild(CardNumberHostComponent) cardNumberHostComponent: CardNumberHostComponent;
    @ViewChild(MonthYearSelectsComponent) monthYearSelectsComponent: MonthYearSelectsComponent;

    private UNSELECTED = -1
    private cardUrl: string
    private cardInputHeight: string = '80px'
    private isInitialized = false
    private cardNumberEditor: Def.Editor = null
    private isStatic = false

    cardConfig: Def.Config;

    formValues = {
        nameOnCard: "",
        defaultPaymentMethod: false,
        cardMonth: this.UNSELECTED,
        cardYear: this.UNSELECTED,
        securityCode: "",
        nickName: ""
    }

    formErrors = {
        nameOnCard: false,
        securityCode: false,
        monthYear: false,
        nickName: false,
        card: false,
    }

    constructor(@Inject(AppDataService) private appDataService: AppDataService,
                @Inject(WindowToken) private $window: Window,
                @Inject(MatDialog) private dialog: MatDialog) {}

    ngOnInit() {
        this.cardConfig = {
            hoapass_url: this.appDataService.appConfig.get_APP_ENV().HOAPASS_API_URL,
            digital_payment_url: this.appDataService.appConfig.get_APP_ENV().DIGITAL_PAYMENT_URL,
            log: false,
            debug: false,
            show_debug_state: false,
        };
        this.cardUrl = this.cardConfig.digital_payment_url + "/card/"
    }

    ngAfterViewInit() {
        this.setup();
    }

    onChange(modelControl: NgModel) {

        if (modelControl.pristine) {
            return;
        }

        switch (modelControl.name) {
            case "nameOnCard":
                this.formErrors.nameOnCard = !(modelControl.status === "VALID")
                break;
            case "securityCode":
                this.formErrors.securityCode = !(modelControl.value.length === 3 || modelControl.value.length === 4);
                break;
            case "cardNickname":
                this.formErrors.nickName = !(modelControl.status === "VALID");
                break;

        }

        this.validateFormAndEmit();
    }

    validateFormAndEmit() {
        if(this.allRequiredFields() && this.noErrors()) {
            this.onFormComplete.emit(true);
        } else {
            this.onFormComplete.emit(false);
        }
    }

    allRequiredFields(): boolean {
        let returnBool = false;

        if(this.formValues.nameOnCard !== "" &&
            this.formValues.securityCode !== "" &&
            this.formValues.cardMonth > this.UNSELECTED &&
            this.formValues.cardYear > this.UNSELECTED ) {
            returnBool = true;
        }

        if(returnBool && this.showNickName){
            return this.formValues.nickName !== "";
        } else {
            return returnBool;
        }
    }

    noErrors(): boolean {
        return !this.formErrors.nameOnCard &&
            !this.formErrors.card &&
            !this.formErrors.monthYear &&
            !this.formErrors.securityCode &&
            !this.formErrors.nickName
    }

    getFormData(): CardDetailEditData {
        let returnObj: CardDetailEditData = {
            makeDefault: this.formValues.defaultPaymentMethod,
            nameOnCard: this.formValues.nameOnCard,
            expMonth: this.formValues.cardMonth,
            expYear: this.formValues.cardYear,
            secCode: this.formValues.securityCode,
        }

        if (this.showNickName) {
            returnObj.nickName = this.formValues.nickName;
        }

        return returnObj;
    }

    monthYearModelChange(data: MonthYearData) {
        this.formErrors.monthYear = !data.isValid;
        this.formValues.cardYear = data.yearValue;
        this.formValues.cardMonth = data.monthValue;
        this.validateFormAndEmit();
    }

    clearCard(){
        if (this.isGuest) {
            this.onClearCard.emit();
        } else {
            this.formValues.nameOnCard = "";
            this.formValues.cardMonth = this.UNSELECTED;
            this.formValues.cardYear = this.UNSELECTED;
            this.formValues.securityCode = "";
            this.monthYearSelectsComponent.setValues(this.UNSELECTED, this.UNSELECTED);
        }
    }

    showSecurityCodeHelp() {
        this.dialog.open(CardCcvHelperDialog, {});
    }

    setup(props = this.props) {
        if ((props.existingCard == null && this.isStatic) || (props.existingCard != null && !this.isStatic)) {
            // if props existing is different than current mode
            this.isInitialized = false
            // allow the editor to be reset
        }

        if (this.isInitialized || this.cardConfig == null) return
        this.isInitialized = true

        const setEditor = (editor: Def.Editor) => {
            this.cardNumberEditor = editor
            // if (props.onCreate != null) {
            //     props.onCreate(this)
            // }
            if (this.props.existingCard) {
                // this.setState({
                //     cardNumberHealth: Object.assign({}, ZERO_HEALTH, { isValid: true })
                // }, () => this.emitUpdate())
                // if existing card then ensure it is known to be valid
            }
            this.prefillForm()
        }

        if (props.existingCard != null) {
            const cardUpdate = props.existingCard
            this.isStatic = true

            this.cardNumberHostComponent.editorCreator.editExisting(
                this.cardConfig,
                props.existingCard.maskedCardNumber.slice(-4),
                props.existingCard.cardBrand,
            ).then(setEditor)
        } else {
            this.isStatic = false
            this.cardNumberHostComponent.editorCreator.createNew(
                { ...this.cardConfig, debug: true }, {
                    onTouched: (isTouched) => {
                        this.cardNumberHostComponent.updateTouched(isTouched);
                    },
                    onValidated: (isValid, errors = []) => {
                        this.cardNumberHostComponent.handleResults(isValid, errors);
                        this.formErrors.card = !isValid;
                        this.validateFormAndEmit();
                    }
                }
            ).then(setEditor)
        }
    }

    prefillForm() {
        if (this.props.existingCard != null) {
            const cardUpdate = this.props.existingCard

            this.formValues = {
                nameOnCard: cardUpdate.cardHolderName,
                defaultPaymentMethod: this.props.makeDefault,
                cardMonth: cardUpdate.expirationMonth,
                cardYear: cardUpdate.expirationYear,
                securityCode: "",
                nickName: this.props.nickName,
            }

            this.monthYearSelectsComponent?.setValues(cardUpdate.expirationMonth, cardUpdate.expirationYear);

        }
    }

    updateCard(
        payerId: number,
        paymentToken: number,
        maskedCardNumber: string,
        cardBrand: string,
        billingAddress: Def.BillingAddress,
    ): Promise<Def.CardPaymentMethodUpdate> {

        const update: Def.CardPaymentMethodUpdate = {
            paymentToken: paymentToken,
            maskedCardNumber: maskedCardNumber,
            cardBrand: cardBrand,
            billingAddress: billingAddress,
            cardHolderName: this.formValues.nameOnCard,
            expirationMonth: this.formValues.cardMonth,
            expirationYear: this.formValues.cardYear,
            securityCode: this.formValues.securityCode,
            customerId: payerId,
        };

        return this.cardNumberEditor.updateCard(update);

    };
}
